"use client";

import { useEffect, useState } from "react";

// Approximate distance of the ring's center from the viewport's
// bottom-right corner — splits the difference between the widget's mobile
// offset (bottom-5/right-5, 20px) and sm+ offset (bottom-8/right-8, 32px)
// plus roughly half the ring's own size. Exact precision doesn't matter
// here: once a dark band is tall enough to be visible near the bottom of
// the viewport at all, it already covers the ring's whole small footprint,
// so being off by a few px either way doesn't change the result.
const PROBE_OFFSET_PX = 70;

/**
 * Reports whether the chat launcher's fixed bottom-right position is
 * currently over a dark page section, so its decorative ring text can
 * switch to a light color scheme there instead of vanishing against a dark
 * background. Driven by any element marked `data-dark-section` — today
 * that's just the footer's dark band, but more sections can opt in the
 * same way later without this hook needing to change.
 *
 * Compares actual bounding rects against a fixed corner probe point on
 * scroll, rather than reaching for IntersectionObserver rootMargin
 * percentages — the widget's on-screen position is a small constant offset
 * from the corner, not a proportion of the viewport, so percentage-based
 * margins couldn't express it precisely anyway.
 */
export function useIsOverDarkSection(): boolean {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    let frame: number | null = null;

    const check = () => {
      frame = null;
      const probeX = window.innerWidth - PROBE_OFFSET_PX;
      const probeY = window.innerHeight - PROBE_OFFSET_PX;

      const sections = document.querySelectorAll<HTMLElement>("[data-dark-section]");
      for (const section of sections) {
        const rect = section.getBoundingClientRect();
        if (
          probeX >= rect.left &&
          probeX <= rect.right &&
          probeY >= rect.top &&
          probeY <= rect.bottom
        ) {
          setIsDark(true);
          return;
        }
      }
      setIsDark(false);
    };

    const scheduleCheck = () => {
      if (frame !== null) return;
      frame = requestAnimationFrame(check);
    };

    check();
    window.addEventListener("scroll", scheduleCheck, { passive: true });
    window.addEventListener("resize", scheduleCheck);
    return () => {
      window.removeEventListener("scroll", scheduleCheck);
      window.removeEventListener("resize", scheduleCheck);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return isDark;
}
